import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "@/api/client";
import { AlertTriangle, AlertCircle, ChevronRight, Wallet, MousePointerClick, Target, Eye, TrendingUp } from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("tr-TR").format(n ?? 0);
const tl = (n) => `${fmt(Math.round((n ?? 0)))} ₺`;

export default function GoogleAdsPage() {
  const [accounts, setAccounts] = useState([]); const [alerts, setAlerts] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    Promise.all([apiClient.get("/ads/accounts").then(r=>r.data), apiClient.get("/ads/alerts").then(r=>r.data)]).then(([a,al]) => { setAccounts(a); setAlerts(al); });
  }, []);

  const totals = accounts.reduce((acc, a) => ({
    spend: acc.spend + (a.totals?.spend_7d || 0),
    clicks: acc.clicks + (a.totals?.clicks_7d || 0),
    conv: acc.conv + (a.totals?.conv_7d || 0),
    impressions: acc.impressions + (a.totals?.impressions_7d || 0),
  }), { spend: 0, clicks: 0, conv: 0, impressions: 0 });

  return (
    <div className="space-y-5" data-testid="ads-page">
      <div>
        <h1 className="text-2xl font-bold">Google Ads MCC</h1>
        <p className="text-sm text-[#64748B] mt-1">{accounts.length} hesap · Son 7 gün</p>
        <div className="inline-flex items-center gap-2 mt-2 text-[11px] font-mono text-[#94A3B8] bg-[#FFFBEB] border border-[#FDE68A] rounded-md px-2.5 py-1"><span className="ky-dot bg-[#F59E0B] ky-pulse"/>DEMO veri — gerçek MCC entegrasyonu beklemede</div>
      </div>

      {alerts.length > 0 && (
        <div className="ky-card p-4" data-testid="ads-alerts">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-3">Uyarılar ({alerts.length})</div>
          <div className="space-y-2">
            {alerts.map((a, i) => (
              <div key={i} className={`flex items-start gap-3 px-3 py-2.5 rounded-lg border ${a.severity === "kritik" ? "border-[#FECACA] bg-[#FEF2F2]" : "border-[#FDE68A] bg-[#FFFBEB]"}`}>
                {a.severity === "kritik" ? <AlertTriangle size={14} className="text-[#DC2626] mt-0.5 shrink-0"/> : <AlertCircle size={14} className="text-[#D97706] mt-0.5 shrink-0"/>}
                <div className="flex-1 min-w-0"><div className="text-sm font-medium">{a.account_name}</div><div className="text-xs text-[#64748B]">{a.message}</div></div>
                <div className="text-xs font-mono text-[#64748B]">{tl(a.balance)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI label="Toplam Harcama" value={tl(totals.spend)} icon={Wallet}/>
        <KPI label="Toplam Tıklama" value={fmt(totals.clicks)} icon={MousePointerClick}/>
        <KPI label="Toplam Gösterim" value={fmt(totals.impressions)} icon={Eye}/>
        <KPI label="Toplam Dönüşüm" value={fmt(totals.conv)} icon={Target} tone="success"/>
      </div>

      <div className="ky-card overflow-hidden">
        <div className="px-5 py-3 border-b border-[#ECECEF] bg-[#FAFAFB]"><div className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Hesaplar</div></div>
        <table className="w-full text-sm">
          <thead className="bg-white border-b border-[#ECECEF]">
            <tr><Th>Hesap</Th><Th>Durum</Th><Th right>Bakiye</Th><Th right>Harcama 7g</Th><Th right>Tıklama</Th><Th right>Dönüşüm</Th><Th right>CTR</Th><Th right>ROAS</Th><Th></Th></tr>
          </thead>
          <tbody>
            {accounts.map(a => (
              <tr key={a.id} onClick={() => nav(`/google-ads/${a.id}`)} className="border-b border-[#ECECEF] ky-row cursor-pointer" data-testid={`ads-account-${a.id}`}>
                <td className="px-5 py-3"><div className="font-medium">{a.name}</div><div className="text-[11px] font-mono text-[#94A3B8]">{a.id}</div></td>
                <td className="px-5 py-3"><span className={`ky-badge ${a.status === "active" ? "ky-badge-success" : "ky-badge-danger"}`}>{a.status === "active" ? "Aktif" : "Durduruldu"}</span></td>
                <td className="px-5 py-3 text-right font-mono"><span className={a.balance < 500 ? "text-[#EF4444] font-semibold" : ""}>{tl(a.balance)}</span></td>
                <td className="px-5 py-3 text-right font-mono">{tl(a.totals?.spend_7d)}</td>
                <td className="px-5 py-3 text-right font-mono">{fmt(a.totals?.clicks_7d)}</td>
                <td className="px-5 py-3 text-right font-mono text-[#10B981]">{fmt(a.totals?.conv_7d)}</td>
                <td className="px-5 py-3 text-right font-mono">%{a.totals?.ctr}</td>
                <td className="px-5 py-3 text-right font-mono">{a.totals?.roas}x</td>
                <td className="px-5 py-3"><ChevronRight size={14} className="text-[#94A3B8]"/></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function KPI({ label, value, icon: Icon, tone }) { const cls = tone === "success" ? "text-[#10B981]" : "text-[#0F172A]"; return <div className="ky-card p-5"><div className="flex justify-between text-[#64748B]"><span className="text-[11px] font-medium uppercase tracking-wider">{label}</span><Icon size={14}/></div><div className={`text-2xl font-semibold tabular-nums mt-2 ${cls}`}>{value}</div></div>; }
function Th({ children, right }) { return <th className={`text-${right?"right":"left"} text-[11px] uppercase tracking-wider text-[#64748B] font-semibold px-5 py-3`}>{children}</th>; }
